import { useEffect, useState } from "react";
import useRenderConfig from "../../utils/useRenderConfig";
import useRenderOnDemand from "../../utils/useRenderOnDemand";
import { ImageFormat, ImageRequest, ImageStatus, ImageType } from "../../utils/useRenderOnDemand.types";
import TurntableTpl from "./TurntableTpl";
import DataContainer from "../../services/DataContainer";
import { MarketSettings, RodSettings } from "../../enum/enums";
import { useParams } from "react-router-dom";
import Spinner from "../LoadingSpinner";
import ErrorMessage from "../ErrorMessage/ErrorMessage";

const Turntable = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [imageStatus, setImageStatus] = useState<any>("");
  const [hasTimedOut, setHasTimedOut] = useState<boolean>(false);
  const [retry, setRetry] = useState<boolean>(false);

  const {modelName} = useParams();
  const {modelNameFromUrl} = useParams();
  const steeringwheel_position = DataContainer.sectionKey(MarketSettings.MARKET_SETTINGS, MarketSettings.STEERINGWHEEL_POSITION);  
    
  const renderConfig = useRenderConfig();

  const renderSpec: ImageRequest = {
    sequence: "360",
    attributes: { 
      stage: "outdoor",
      steeringwheel_position: steeringwheel_position,
    },
    renderVersion: parseInt(DataContainer.sectionKey(RodSettings.ROD_SETTINGS, `${modelName ? modelName.split("_")[0] : modelNameFromUrl?.split("_")[0]}_renderversion`)),
    configuration: renderConfig,
    format: ImageFormat.JPEG,
    width: 1920,
    height: 1440,
    type: ImageType.Image,
    compressionQuality: 70,
  };

  const response = useRenderOnDemand(renderSpec);

  useEffect(() => {
    if (hasTimedOut){
      setHasTimedOut(false);
    }

    if (response.status === ImageStatus.Queued) {
      if (imageStatus !== ImageStatus.Ready) {
        setImageStatus(ImageStatus.Ready);
      }
    } else if (response.status !== imageStatus) {
      setImageStatus(response.status);
    }

    if (imageStatus === ImageStatus.None) {
      setIsLoading(true);
    } else if (response.status === ImageStatus.Ready) {
      setRetry(false);
      if (response.urlAssetsStatus.some(status => status === "false")){
        setRetry(true);
      }
      else {
        setIsLoading(false);
      }
    } else if (imageStatus === ImageStatus.Timeout) {
      setHasTimedOut(true);
      setIsLoading(false);
    }
  }, [response, imageStatus]);

  useEffect(() => {
    return () => setImageStatus(null);
  }, []);

  useEffect(() => {
    if (response.status === "none"){
      return;
    }
    const responseHandler = async (res: any) => {
      if (res.data.urlAssetsStatus.some((status: string) => status === "false")) {
        res.data.urlAssetsStatus = [];
        let tries = 0;
        res.data.tries = tries;
        let retry = true;
        while (retry) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const reResponse = await DataContainer.checkStatus(res.data);
          if (reResponse.data.urlAssetsStatus.some((status: boolean) => !status)) {
            reResponse.data.urlAssetsStatus = [];
            tries++;
            res.data.tries = tries;
            if (tries > 15) {
              retry = false;
            }
          } else {
            tries++;
            res.data.tries = tries;
            retry = false;
          }
          tries++;
          res.data.tries = tries;
          if (tries > 15) {
            retry = false;
          }
        }
        setIsLoading(false);
      }
    };
    if (imageStatus !== ImageStatus.Ready){
      DataContainer.getRender(renderSpec).then(responseHandler);
    }
    else {
      setIsLoading(false);
      setRetry(false);
    }

  }, [retry]);

  return (
    (hasTimedOut) ?
      <div className="w-full h-full mx-auto flex max-h-full max-w-full flex-grow">
        <ErrorMessage renderSpec={renderSpec} setIsLoading={setIsLoading} setHasTimedOut={setHasTimedOut} />
      </div>
      : isLoading ? (
        <div className="w-full h-full mx-auto flex max-h-full max-w-full flex-grow">
          <Spinner className="w-full max-h-full" size={50} color="#000" />
        </div>
      ) : (
        <TurntableTpl
          images={response.urlAssets}
          duration={0.6}
        />)
  );
};

export default Turntable;